import { useLayoutEffect, useRef, useState } from 'react'
import { ChevronDown, X } from 'react-feather'
import Select, { components } from 'react-select'
import { Spinner } from 'reactstrap'
import './select.css'

const ClearIndicator = (props) => (
  <components.ClearIndicator {...props}>
    <X size={18} />
  </components.ClearIndicator>
)

const DropdownIndicator = (props) => {
  const { isLoading } = props.selectProps
  if (isLoading) {
    return (
      <components.DropdownIndicator {...props}>
        <Spinner size="sm" color="primary" />
      </components.DropdownIndicator>
    )
  }
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDown size={18} />
    </components.DropdownIndicator>
  )
}

// CUSTOM INPUT — forwards testid to the underlying <input>
const SelectInput = (props) => {
  const { selectProps } = props
  return (
    <components.Input
      {...props}
      data-testid={
        selectProps?.testId ? `${selectProps.testId}-input` : undefined
      }
    />
  )
}

// CUSTOM CONTROL — forwards testid to the control element
const SelectControl = (props) => {
  const { selectProps, ...rest } = props
  return (
    <components.Control
      {...rest}
      innerProps={{
        ...rest.innerProps,
        ...(selectProps?.testId
          ? { 'data-testid': selectProps.testId }
          : {}),
      }}
    />
  )
}

// CUSTOM MULTI VALUE
const SelectedChip = ({ data, onRemove, measuring = false, summary = false, withCounter = false }) => {
  return (
    <div
      data-measure-chip={measuring ? '' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        backgroundColor: summary ? '#2f6da9' : '#e0f2ff',
        borderRadius: '12px',
        padding: '2px 4px 2px 8px',
        margin: '2px',
        maxWidth: measuring ? 'none' : withCounter ? 'calc(100% - 88px)' : 'calc(100% - 4px)',
        minWidth: 0,
        flexShrink: 0,
      }}
    >
      {summary && data.image && (
        <img
          src={data.image}
          alt=""
          style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            objectFit: 'cover',
            marginRight: 5,
            flexShrink: 0,
          }}
        />
      )}
      <span
        style={{
          color: summary ? '#fff' : '#0369a1',
          fontSize: '13px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          minWidth: 0,
          flex: '1 1 auto',
        }}
        title={data.label}
      >
        {data.label}
      </span>
      <span
        role={measuring ? undefined : 'button'}
        tabIndex={measuring ? undefined : 0}
        aria-label={measuring ? undefined : `Hapus ${data.label}`}
        style={{
          color: summary ? '#fff' : '#0369a1',
          cursor: 'pointer',
          marginLeft: '4px',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
        onMouseDown={(e) => {
          if (measuring) return
          e.preventDefault()
          e.stopPropagation()
        }}
        onClick={measuring ? undefined : onRemove}
        onKeyDown={measuring ? undefined : (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onRemove(e)
          }
        }}
      >
        <X size={12} />
      </span>
    </div>
  )
}

export const PMBSelectedSummary = ({ label, value = [], options = [], onChange, isDisabled = false, testId = 'pmb-selected-summary' }) => {
  if (!value.length) return null
  const imageByValue = new Map(options.map((option) => [String(option.value), option.image]))
  return (
    <div className="d-flex flex-wrap align-items-center mt-2" data-testid={testId}>
      <span className="me-2" style={{ color: '#6b7280', fontSize: 13 }}>{label}</span>
      {value.map((item) => (
        <SelectedChip
          key={item.value}
          data={{ ...item, image: item.image || imageByValue.get(String(item.value)) }}
          summary
          onRemove={() => {
            if (!isDisabled) onChange(value.filter((selected) => selected.value !== item.value))
          }}
        />
      ))}
    </div>
  )
}

const MultiValue = ({ removeProps, data, index, selectProps }) =>
  index < selectProps.visibleTagCount ? (
    <SelectedChip data={data} onRemove={removeProps.onClick} withCounter={selectProps.selectedCount > selectProps.visibleTagCount} />
  ) : null

const ValueContainer = ({ children, selectProps, ...props }) => (
  <components.ValueContainer selectProps={selectProps} {...props}>
    {children}
    {selectProps.selectedCount > selectProps.visibleTagCount && (
      <span
        data-testid={`${selectProps.testId}-more-counter`}
        style={{
          position: 'absolute',
          right: 2,
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: '#2f6da9',
          color: '#fff',
          borderRadius: 12,
          padding: '2px 8px',
          fontSize: 12,
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        +{selectProps.selectedCount - selectProps.visibleTagCount} lainnya
      </span>
    )}
  </components.ValueContainer>
)

const PMBSelect = ({
  value,
  onChange,
  options,
  isMulti = false,
  isInvalid = false,
  placeholder = 'Pilih...',
  onInputChange,
  onMenuScrollToBottom,
  title,
  menuPlacement = 'auto',
  menuPosition: customMenuPosition,
  menuPortalTarget: customMenuPortalTarget,
  placeholderColor,
  isLoading = false,
  isDisabled = false,
  testId = 'pmb-select',
  hideOverflowChips = false,
  formatOptionLabel: customFormatOptionLabel,
  filterOption: customFilterOption,
  formatGroupLabel,
  ...props
}) => {
  const rootRef = useRef(null)
  const selectedValues = isMulti && Array.isArray(value) ? value : []
  const [visibleTagCount, setVisibleTagCount] = useState(selectedValues.length)

  const resolvedMenuPortalTarget =
    customMenuPortalTarget !== undefined
      ? customMenuPortalTarget
      : typeof document !== 'undefined'
      ? document.body
      : undefined

  const menuPosition =
    customMenuPosition || (resolvedMenuPortalTarget === null ? 'absolute' : 'fixed')

  useLayoutEffect(() => {
    if (!isMulti) return undefined

    const measure = () => {
      const container = rootRef.current?.querySelector('.pmb-select__value-container')
      const chips = rootRef.current?.querySelectorAll('[data-measure-chip]')
      if (!container || !chips || !container.clientWidth) return

      // Keep room for the input caret; the clear and dropdown buttons are
      // outside the value container and already excluded from its width.
      const widths = Array.from(chips, (chip) => chip.getBoundingClientRect().width + 4)
      const fit = (availableWidth) => {
        let usedWidth = 0
        let count = 0
        widths.forEach((width, index) => {
          if (index === count && (count === 0 || usedWidth + width <= availableWidth)) {
            usedWidth += width
            count += 1
          }
        })
        return count
      }
      const availableWidth = Math.max(0, container.clientWidth - 24)
      let count = fit(availableWidth)
      if (count < widths.length) count = fit(Math.max(0, availableWidth - 84))
      setVisibleTagCount((current) => current === count ? current : count)
    }

    measure()
    const observer = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(measure)
      : null
    const control = rootRef.current?.querySelector('.pmb-select__control')
    if (control) observer?.observe(control)
    window.addEventListener('resize', measure)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [isMulti, value])

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: isInvalid ? '#dc3545' : '#d1d5db',
      boxShadow: 'none',
      '&:hover': {
        borderColor: isInvalid ? '#dc3545' : '#d1d5db',
      },
      borderRadius: '8px',
      padding: '2px',
      ...(isMulti ? { height: '42px', minHeight: '42px' } : {}),
      minWidth: props.style?.minWidth || '100%',
      maxWidth: props.style?.maxWidth || '100%',
      backgroundColor:
        state.isDisabled || isLoading ? '#f8f9fa' : provided.backgroundColor,
      opacity: state.isDisabled || isLoading ? 0.7 : 1,
      cursor: state.isDisabled || isLoading ? 'not-allowed' : 'default',
    }),
    valueContainer: (provided) => ({
      ...provided,
      position: 'relative',
      flexWrap: 'nowrap',
      overflow: isMulti ? 'hidden' : 'visible',
      padding: '2px 4px',
    }),
    singleValue: (provided) => ({
      ...provided,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '100%',
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: props.menuPortalZIndex || 9999,
      ...(props.styles?.menuPortal ? props.styles.menuPortal(base) : {}),
    }),
    menu: (base) => ({
      ...base,
      zIndex: 10,
      ...(props.styles?.menu ? props.styles.menu(base) : {}),
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#6b7280',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#6b7280',
    }),
    option: (provided, state) => ({
      ...provided,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      backgroundColor: state.isDisabled
        ? '#f9fafb'
        : state.isSelected
        ? '#0369a1'
        : state.isFocused
        ? '#f0f9ff'
        : provided.backgroundColor,
      color: state.isDisabled
        ? '#9ca3af'
        : state.isSelected
        ? '#fff'
        : '#1f2937',
    }),
    placeholder: (defaultStyles, state) => ({
      ...defaultStyles,
      color:
        placeholderColor || state.isDisabled || isLoading
          ? '#6c757d'
          : defaultStyles.color,
    }),
  }

  const getOptionLabel = (e) => (
    <div
      className="d-flex align-items-center justify-content-between w-100"
      style={{
        opacity: e.isDisabled ? 0.65 : 1,
      }}
    >
      <div className="d-flex align-items-center">
        {e.image && (
          <img
            src={e.image}
            alt={e.label}
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              marginRight: 8,
              objectFit: 'cover',
            }}
          />
        )}
        <span
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {e.label}
        </span>
      </div>
      {e.disabledReason && (
        <span
          style={{
            fontSize: '11px',
            backgroundColor: '#fee2e2',
            color: '#b91c1c',
            border: '1px solid #fecaca',
            padding: '2px 8px',
            borderRadius: '9999px',
            marginLeft: '8px',
            whiteSpace: 'nowrap',
            fontWeight: 500,
          }}
        >
          {e.disabledReason}
        </span>
      )}
    </div>
  )

  return (
    <div ref={rootRef}>
      {title && (
        <label className="d-block mb-1" data-testid={`${testId}-label`}>
          {title}
        </label>
      )}

      <Select
        isMulti={isMulti}
        visibleTagCount={visibleTagCount}
        selectedCount={selectedValues.length}
        data-testid={testId}
        testId={testId}
        value={value}
        onChange={onChange}
        options={options}
        styles={customStyles}
        placeholder={placeholder}
        classNamePrefix="pmb-select"
        onInputChange={onInputChange}
        onMenuScrollToBottom={onMenuScrollToBottom}
        getOptionValue={(e) => e.value}
        getOptionLabel={(e) => e.label}
        formatOptionLabel={customFormatOptionLabel || getOptionLabel}
        filterOption={customFilterOption}
        formatGroupLabel={formatGroupLabel}
        menuPlacement={menuPlacement}
        menuPosition={menuPosition}
        menuPortalTarget={resolvedMenuPortalTarget}
        isLoading={isLoading}
        isDisabled={isDisabled || isLoading}
        hideSelectedOptions={false}
        components={{
          Control: SelectControl,
          Input: SelectInput,
          ClearIndicator,
          DropdownIndicator,
          ...(isMulti ? { MultiValue, ValueContainer } : {}),
        }}
        {...props}
      />
      {isMulti && !hideOverflowChips && selectedValues.length > visibleTagCount && (
        <div className="d-flex flex-wrap align-items-center mt-2" data-testid={`${testId}-extra-chips`}>
          {selectedValues.slice(visibleTagCount).map((item) => (
            <SelectedChip
              key={item.value}
              data={item}
              onRemove={() => {
                if (!isDisabled && !isLoading) {
                  onChange(selectedValues.filter((selected) => selected.value !== item.value))
                }
              }}
            />
          ))}
        </div>
      )}
      {isMulti && (
        <div aria-hidden="true" style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', display: 'flex', width: 'max-content' }}>
          {selectedValues.map((item) => (
            <SelectedChip key={item.value} data={item} measuring />
          ))}
        </div>
      )}
    </div>
  )
}

export default PMBSelect
